import {
  type StepKind,
  type StepPartData,
  type TimelineRow,
  timelineRows,
  turnDurationMs,
} from "@esi/shared";
import {
  Bookmark,
  Brain,
  ChevronDown,
  ChevronRight,
  Code,
  EyeOff,
  FilePen,
  FileText,
  Globe,
  Layers,
  LifeBuoy,
  ListTodo,
  type LucideIcon,
  MessageCircleQuestionMark,
  PenLine,
  Search,
  Users,
  Wrench,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ClassificationMark } from "./classification.tsx";

/**
 * The step timeline of an answer (openspec change `agent-turn`, D4, D5): one collapsed line
 * above the answer with the step kinds and the turn's duration, expandable to one row per step
 * with its icon, label, duration and details. While the answer streams, the line shows the
 * current step and replaces the fixed progress steps. Durations come from the server's step
 * times; only the running clock of a live turn is the browser's own.
 */

const ICONS: Record<StepKind, LucideIcon> = {
  thinking: Brain,
  search: Search,
  read: FileText,
  web: Globe,
  wiki_write: FilePen,
  artefact: Layers,
  code: Code,
  task: ListTodo,
  memory: Bookmark,
  question: MessageCircleQuestionMark,
  people: Users,
  answer: PenLine,
  other: Wrench,
};

/** Tools of the `other` kind that have their own words. */
const OTHER_TOOLS: Readonly<Record<string, { label: "help" | "feedback"; icon: LucideIcon }>> = {
  product_help: { label: "help", icon: LifeBuoy },
  draft_feedback: { label: "feedback", icon: Wrench },
};

const otherOf = (row: TimelineRow) =>
  row.kind === "other" ? OTHER_TOOLS[row.steps[0]?.calls[0]?.name ?? ""] : undefined;

/**
 * What a task or memory row did (agent-turn D10–D13), by its tools: proposals first, then the
 * read with how many tasks or memories it returned. Counts only, never task or memory text.
 */
function taskMemoryOf(row: TimelineRow) {
  const calls = row.steps.flatMap((s) => s.calls);
  const named = (...names: string[]) => calls.filter((c) => names.includes(c.name));
  const read = (name: string) => named(name).reduce((sum, c) => sum + (c.count ?? 0), 0);
  if (row.kind === "task") {
    // Cards shown: a refused call counts 0 (older steps without a count count 1).
    const attempts = named("create_task", "update_task");
    const proposed = attempts.reduce((sum, c) => sum + (c.count ?? 1), 0);
    return attempts.length > 0
      ? { label: "proposedTask" as const, count: proposed }
      : { label: "readTasks" as const, count: read("list_tasks") };
  }
  if (row.kind === "memory") {
    return named("propose_memory").length > 0
      ? { label: "proposedMemory" as const, count: 1 }
      : { label: "recalled" as const, count: read("recall_memory") };
  }
  return undefined;
}

/** Whole seconds, at least 0. */
const secondsOf = (ms: number) => Math.max(0, Math.round(ms / 1000));

function Duration({ ms }: { ms: number }) {
  const text = useDurationText();
  return (
    <time dateTime={`PT${secondsOf(ms)}S`} className="tabular-nums">
      {text(ms)}
    </time>
  );
}

function useDurationText() {
  const { t } = useTranslation();
  return (ms: number) => {
    const s = secondsOf(ms);
    // Under one second reads "< 1 s", never "0 s" (user, 2026-11-03).
    if (s < 1) {
      return t("conversation.timeline.underSecond");
    }
    return s < 60
      ? t("conversation.timeline.seconds", { count: s })
      : t("conversation.timeline.minutes", { minutes: Math.floor(s / 60), seconds: s % 60 });
  };
}

/**
 * A second-by-second clock while `on`: the time since the turn's first step started, on the
 * server's times shifted by the gap seen between the server's start of the latest step and its
 * arrival here (so a continuation or a remount keeps counting); before any step, since mount.
 */
function useElapsed(on: boolean, steps: readonly StepPartData[]): number {
  const mounted = useRef(Date.now());
  const skew = useRef<{ n: number; ms: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) {
      return;
    }
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [on]);
  const latest = steps.at(-1);
  if (latest && skew.current?.n !== latest.n) {
    const seen = Date.now() - Date.parse(latest.startedAt);
    skew.current = { n: latest.n, ms: skew.current ? Math.min(skew.current.ms, seen) : seen };
  }
  const first = steps[0];
  return first && skew.current
    ? Math.max(0, now - (Date.parse(first.startedAt) + skew.current.ms))
    : Math.max(0, now - mounted.current);
}

export interface TimelineProps {
  steps: readonly StepPartData[];
  /** The answer is still being written. */
  live?: boolean;
  /** The text is done and its citations are being checked. */
  checking?: boolean;
  /**
   * The row of one of a step's tool calls (outbound call, help, wiki write, artefact), built
   * only when the timeline is open; undefined when that call has none.
   */
  renderEmbed?: (toolCallId: string) => ReactNode | undefined;
}

export function Timeline(props: TimelineProps) {
  const { t } = useTranslation();
  const duration = useDurationText();
  const { steps, live = false, checking = false } = props;
  const [open, setOpen] = useState(false);
  const rowsId = useId();
  const rows = useMemo(() => timelineRows(steps), [steps]);
  const elapsed = useElapsed(live || checking, steps);
  const documents = rows
    .filter((r) => r.kind === "read")
    .reduce((sum, r) => sum + r.documentCount, 0);

  const doneLabel = (row: TimelineRow, total?: number): string => {
    const other = otherOf(row);
    if (other) {
      return t(`conversation.timeline.done.${other.label}`);
    }
    if (row.kind === "read") {
      const count = total ?? row.documentCount;
      return count > 0
        ? t("conversation.timeline.done.read", { count })
        : t("conversation.timeline.done.readNone");
    }
    const tm = taskMemoryOf(row);
    if (tm) {
      return t(`conversation.timeline.done.${tm.label}`, { count: tm.count });
    }
    return t(`conversation.timeline.done.${row.kind}`);
  };
  const runningLabel = (row: TimelineRow): string => {
    if (row.thinking && row.kind === "thinking") {
      return t("conversation.timeline.running.thinking");
    }
    const tm = taskMemoryOf(row);
    return tm
      ? t(`conversation.timeline.running.${tm.label}`)
      : t(`conversation.timeline.running.${row.kind}`);
  };

  // The collapsed line: each kind once, in order, reads counted over the whole turn.
  const kinds: string[] = [];
  for (const row of rows) {
    if (row.running) {
      continue;
    }
    const label = doneLabel(row, row.kind === "read" ? documents : undefined);
    if (!kinds.includes(label)) {
      kinds.push(label);
    }
  }
  const current = rows.findLast((r) => r.running);
  const liveNow = live || checking;
  const currentLabel = current
    ? runningLabel(current)
    : checking
      ? t("conversation.timeline.running.checking")
      : t("conversation.timeline.running.starting");
  const total = turnDurationMs(steps);

  return (
    <div
      className="flex flex-col text-xs text-fg-muted"
      data-testid="timeline"
      // While the turn runs, the line is its progress (what journeys wait on to end).
      {...(liveNow && { role: "group", "aria-label": t("conversation.timeline.progress") })}
    >
      {/* The duration and the phone's "+N" sit beside the button, not in it: its name lists
          every kind, stays put while the clock ticks, and holds every word the button shows
          (WCAG 2.5.3). */}
      <div className="flex min-w-0 items-center">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={rowsId}
          aria-label={`${t("conversation.timeline.label")}: ${liveNow ? `${currentLabel} · ` : ""}${kinds.join(" · ")}`}
          onClick={() => setOpen((o) => !o)}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-sm py-0.5 text-start hover:text-fg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
          {liveNow ? (
            <span
              aria-hidden="true"
              className="size-3.5 shrink-0 rounded-full border-2 border-line border-t-fg-secondary motion-safe:animate-spin"
            />
          ) : open ? (
            <ChevronDown aria-hidden="true" className="size-3 shrink-0" strokeWidth={2} />
          ) : (
            <ChevronRight aria-hidden="true" className="size-3 shrink-0" strokeWidth={2} />
          )}
          <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden whitespace-nowrap">
            {liveNow ? (
              <>
                <span className="font-medium text-fg-secondary">{currentLabel}</span>
                {kinds.length > 0 ? (
                  <>
                    <span aria-hidden="true" className="text-line-strong">
                      ·
                    </span>
                    <span className="truncate">{kinds.join(", ")}</span>
                  </>
                ) : null}
              </>
            ) : (
              kinds.map((kind, i) => (
                <span
                  key={kind}
                  className={cn("flex items-center gap-1.5", i > 1 && "max-sm:hidden")}
                >
                  {i > 0 ? (
                    <span aria-hidden="true" className="text-line-strong">
                      ·
                    </span>
                  ) : null}
                  <span className="font-medium text-fg-secondary">{kind}</span>
                </span>
              ))
            )}
          </span>
        </button>
        {!liveNow && kinds.length > 2 ? (
          // Hidden from the tree: the button's name already lists every kind.
          <span aria-hidden="true" className="flex shrink-0 items-center gap-1.5 ps-1.5 sm:hidden">
            <span className="text-line-strong">·</span>
            <span className="font-medium text-fg-secondary">
              {t("conversation.timeline.more", { count: kinds.length - 2 })}
            </span>
          </span>
        ) : null}
        <span className="ms-auto shrink-0 ps-2">
          {liveNow ? (
            <span className="tabular-nums">{duration(elapsed)}</span>
          ) : total !== undefined ? (
            <Duration ms={total} />
          ) : null}
        </span>
      </div>
      {liveNow ? (
        <p role="status" aria-live="polite" className="sr-only">
          {currentLabel}
        </p>
      ) : null}
      {open ? (
        <ul id={rowsId} className="mt-1 flex flex-col border-t border-line">
          {rows.map((row) => (
            <StepRow
              key={`${row.kind}-${row.thinking ? "t" : ""}${row.steps[0]?.n ?? 0}`}
              row={row}
              label={
                row.thinking
                  ? row.thinking.state === "pending" || row.running
                    ? t("conversation.timeline.running.thinking")
                    : row.durationMs !== undefined
                      ? t("conversation.timeline.thoughtFor", {
                          duration: duration(row.durationMs),
                        })
                      : t("conversation.timeline.done.thinking")
                  : row.running
                    ? runningLabel(row)
                    : doneLabel(row)
              }
              renderEmbed={props.renderEmbed}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function StepRow(props: {
  row: TimelineRow;
  label: string;
  renderEmbed?: ((toolCallId: string) => ReactNode | undefined) | undefined;
}) {
  const { t } = useTranslation();
  const { row } = props;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const Icon = (row.thinking ? undefined : otherOf(row)?.icon) ?? ICONS[row.kind];
  const embedded = row.thinking
    ? []
    : row.steps.flatMap((s) =>
        s.calls.flatMap((c) => {
          const node = props.renderEmbed?.(c.toolCallId);
          return node ? [{ id: c.toolCallId, node }] : [];
        }),
      );
  const thinkingText = row.thinking
    ? row.thinking.state === "done"
      ? (row.thinking.summary ?? null)
      : row.thinking.state === "fallback"
        ? t("conversation.timeline.thoughtFallback")
        : null
    : null;
  const details: ReactNode =
    thinkingText !== null ? (
      <p className="text-[13px] leading-normal text-fg-secondary">{thinkingText}</p>
    ) : row.thinking ? null : (row.kind === "search" ||
        row.kind === "task" ||
        // The topic people were looked for by (agent-turn D23), never who was found.
        row.kind === "people") &&
      row.queries.length > 0 ? (
      <div className="flex flex-col gap-1">
        {row.queries.map((q, i) => (
          <p
            // biome-ignore lint/suspicious/noArrayIndexKey: queries repeat; order is stable
            key={i}
            className="border-s-2 border-line-strong ps-2.5 text-[13px] text-fg-secondary"
          >
            “{q}”
          </p>
        ))}
      </div>
    ) : row.sources.length > 0 || row.hidden > 0 ? (
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
        {row.sources.map((s) => (
          <span
            key={s.documentId}
            className="inline-flex min-w-0 items-center gap-1.5 text-fg-secondary"
          >
            <FileText aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
            <span className="min-w-0 truncate">{s.title}</span>
            {s.classification ? <ClassificationMark id={s.classification} /> : null}
          </span>
        ))}
        {row.hidden > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <EyeOff aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
            {t("audience.hiddenSources", { count: row.hidden })}
          </span>
        ) : null}
      </div>
    ) : null;
  return (
    <li
      data-step-kind={row.thinking ? "thinking" : row.kind}
      className="grid grid-cols-[16px_1fr] items-start gap-x-2 border-b border-line py-1.5 sm:grid-cols-[16px_1fr_auto]"
    >
      <Icon aria-hidden="true" className="mt-0.5 size-3.5 text-fg-muted" strokeWidth={1.75} />
      <span className="min-w-0 font-medium text-fg-secondary">{props.label}</span>
      <span className="col-start-2 text-fg-muted sm:col-start-auto sm:pt-px">
        {row.durationMs !== undefined && !(row.thinking && row.kind !== "thinking") ? (
          <Duration ms={row.durationMs} />
        ) : null}
      </span>
      {details ? (
        <details
          className="col-start-2 mt-0.5 sm:col-end-4"
          open={detailsOpen}
          onToggle={(e) => setDetailsOpen(e.currentTarget.open)}
        >
          <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded-sm text-fg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus [&::-webkit-details-marker]:hidden">
            {detailsOpen ? (
              <ChevronDown aria-hidden="true" className="size-3" strokeWidth={2} />
            ) : (
              <ChevronRight aria-hidden="true" className="size-3" strokeWidth={2} />
            )}
            {detailsOpen
              ? t("conversation.timeline.hideDetails")
              : t("conversation.timeline.details")}
          </summary>
          <div className="mt-1.5 flex flex-col gap-1.5">{details}</div>
        </details>
      ) : null}
      {embedded.length > 0 ? (
        <div className="col-start-2 mt-1 flex flex-col gap-1 sm:col-end-4">
          {embedded.map((e) => (
            <div key={e.id}>{e.node}</div>
          ))}
        </div>
      ) : null}
    </li>
  );
}
